import React, { useState, useEffect } from 'react';
import { Download, X, Share, Plus, Smartphone, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CustomerInstallPrompt = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMac = /Mac/.test(navigator.userAgent) && !/iPhone|iPad|iPod/.test(navigator.userAgent);
  const isWindows = /Windows/.test(navigator.userAgent);
  const isDesktop = !isIOS && !isAndroid && (isMac || isWindows || window.innerWidth >= 1024);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  useEffect(() => {
    // Don't show if already installed as PWA
    if (isStandalone) {
      setShowBanner(false);
      return;
    }

    // Check if user has dismissed banner recently (within 7 days)
    const dismissed = localStorage.getItem('nas-portal-install-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Show banner after a small delay
    setTimeout(() => setShowBanner(true), 2000);

    // Listen for install prompt (Chrome/Edge on desktop and Android)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [isStandalone]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Chrome/Edge - trigger native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
        localStorage.setItem('nas-portal-install-dismissed', new Date().toISOString());
      }
      setDeferredPrompt(null);
    } else {
      // Show manual instructions
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('nas-portal-install-dismissed', new Date().toISOString());
  };

  if (!showBanner || isStandalone) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-xl shadow-lg mb-4 relative mx-4 mt-4" data-testid="install-prompt-banner">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/70 hover:text-white"
          data-testid="dismiss-install-btn"
        >
          <X size={20} />
        </button>
        
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            {isDesktop ? (
              <Monitor className="text-white" size={24} />
            ) : (
              <Smartphone className="text-white" size={24} />
            )}
          </div>
          <div className="flex-1 pr-6">
            <h3 className="font-semibold text-lg">Install NAS Portal</h3>
            <p className="text-green-100 text-sm">
              {isDesktop 
                ? 'Add to your computer for quick access' 
                : 'Add to home screen for easy access'}
            </p>
          </div>
          <Button 
            onClick={handleInstallClick}
            className="bg-white text-green-600 hover:bg-green-50 font-semibold hidden sm:flex"
            data-testid="install-app-btn"
          >
            <Download size={18} className="mr-2" />
            Install
          </Button>
        </div>
        
        {/* Mobile install button */}
        <Button 
          onClick={handleInstallClick}
          className="w-full mt-3 bg-white text-green-600 hover:bg-green-50 font-semibold sm:hidden"
          data-testid="install-app-mobile-btn"
        >
          <Download size={18} className="mr-2" />
          Install App
        </Button>
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-slate-900">Install NAS Portal</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={24} />
                </button>
              </div>
              
              {isIOS ? (
                <>
                  <p className="text-slate-600 mb-6">Install on your <strong>iPhone/iPad</strong>:</p>
                  
                  <div className="space-y-5">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Open in Safari</p>
                        <p className="text-slate-500 text-sm">This works only in Safari browser</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap Share button</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <Share size={20} className="text-blue-600" />
                          <span className="text-sm text-slate-600">Share</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap "Add to Home Screen"</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <Plus size={18} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Add to Home Screen</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap "Add" to confirm</p>
                        <p className="text-slate-500 text-sm">NAS Portal will appear on your home screen!</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : isDesktop ? (
                <>
                  <p className="text-slate-600 mb-6">Install on your <strong>Computer</strong>:</p>
                  
                  <div className="space-y-5">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Use Chrome or Edge</p>
                        <p className="text-slate-500 text-sm">Open this page in Chrome or Edge browser</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Look for install icon</p>
                        <p className="text-slate-500 text-sm">In the address bar (right side), click:</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <Download size={18} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Install icon</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Or use the menu</p>
                        <p className="text-slate-500 text-sm">Click ⋮ menu → "Install NAS Portal..."</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Click "Install"</p>
                        <p className="text-slate-500 text-sm">The app will open in its own window!</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-600 mb-6">Install on your <strong>Android phone</strong>:</p>
                  
                  <div className="space-y-5">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Open in Chrome</p>
                        <p className="text-slate-500 text-sm">Use Google Chrome browser</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap Menu (⋮)</p>
                        <p className="text-slate-500 text-sm">Three dots at top right</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <span className="text-xl text-slate-600">⋮</span>
                          <span className="text-sm text-slate-600">Menu</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap "Install app"</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <Download size={18} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Install app</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap "Install"</p>
                        <p className="text-slate-500 text-sm">NAS Portal will appear on your home screen!</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              <Button 
                onClick={() => setShowInstructions(false)}
                className="w-full mt-6 bg-green-600 hover:bg-green-700"
              >
                Got it!
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerInstallPrompt;
